import type { WorkflowNodeEntity } from '@flowgram.ai/free-layout-editor';

export const IP_GROUP_OUTPUT_PORT_ID = 'ip-group-output';
export const IP_GROUP_GAP = 16;
export const IP_GROUP_PADDING = 30;
export const IP_GROUP_HEADER_HEIGHT = 32;
/** IP 组内媒体节点的标题栏高度，与节点默认渲染规格保持一致。 */
export const IP_GROUP_MEDIA_NODE_HEADER_HEIGHT = 40;
export type IpGroupRole = 'main' | 'turnaround' | 'voice' | 'settings' | 'material';
export type IpGroupAssetType = 'personal' | 'official';

/** 通过持久化组模式识别 IP 组，普通组与旧 IP 引用节点保持兼容。 */
export function isIpGroupNode(node?: Pick<WorkflowNodeEntity, 'flowNodeType' | 'toJSON'> | null): boolean {
  return node?.flowNodeType === 'group' && node.toJSON().data?.groupMode === 'ip';
}

/** 官方 IP 展开到画布后作为只读内容展示，组和成员都统一通过该标记识别。 */
export function isOfficialIpGroupNode(node?: Pick<WorkflowNodeEntity, 'flowNodeType' | 'toJSON'> | null): boolean {
  return isIpGroupNode(node) && node?.toJSON().data?.ipAssetType === 'official';
}

/** 子节点约束跟随实际父组，显式解组后立即恢复普通节点行为。 */
export function isIpGroupChild(node?: Pick<WorkflowNodeEntity, 'parent'> | null): boolean {
  return isIpGroupNode(node?.parent);
}

/** 子节点即使脱离父组渲染，也依据快照标记保持官方 IP 的只读行为。 */
export function isOfficialIpGroupMember(node?: Pick<WorkflowNodeEntity, 'flowNodeType' | 'toJSON' | 'parent'> | null): boolean {
  return node?.toJSON().data?.ipAssetType === 'official' || isOfficialIpGroupNode(node) || isOfficialIpGroupNode(node?.parent);
}

/** IP 组内媒体由 IP 快照统一维护；脱离群组渲染的官方素材也继续保持不可替换。 */
export function isIpGroupMediaReplacementBlocked(
  node?: Pick<WorkflowNodeEntity, 'flowNodeType' | 'toJSON' | 'parent'> | null,
): boolean {
  return isIpGroupChild(node) || isOfficialIpGroupMember(node);
}

/** IP 组成员只由显式素材操作修改，禁止拖拽交换成员。 */
export function canChangeIpGroupMembership(node: WorkflowNodeEntity, target?: WorkflowNodeEntity): boolean {
  return !isIpGroupChild(node) && !isIpGroupNode(target);
}

export interface IpGroupLayoutItem {
  id: string;
  role?: IpGroupRole;
  width: number;
  height: number;
}

export interface IpGroupPositionBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** 以顶部中心坐标为锚点，为新 IP 组寻找不与现有顶层节点重叠的水平落点。 */
export function getIpGroupPosition(
  center: { x: number; y: number; },
  size: { width: number; height: number; },
  bounds: IpGroupPositionBounds[],
  gap = 24,
) {
  let topCenter = { ...center };
  const maxSteps = bounds.length + 1;
  for (let step = 0; step < maxSteps; step += 1) {
    const candidateLeft = topCenter.x - size.width / 2;
    const candidateRight = topCenter.x + size.width / 2;
    const overlapping: IpGroupPositionBounds[] = [];
    for (const bound of bounds) {
      if (candidateLeft < bound.x + bound.width + gap
        && candidateRight > bound.x - gap
        && topCenter.y < bound.y + bound.height + gap
        && topCenter.y + size.height > bound.y - gap) {
        overlapping.push(bound);
      }
    }
    if (!overlapping.length) return topCenter;
    const rightmost = Math.max(...overlapping.map((bound) => bound.x + bound.width));
    topCenter = {
      x: Math.max(candidateRight, rightmost) + gap + size.width / 2,
      y: topCenter.y,
    };
  }
  return topCenter;
}

/**
 * 基础行按角色槽位排序，避免 group.blocks 顺序变化（选中、连线、内容变更）导致主视觉与三视图左右对调。
 *
 * @param {IpGroupLayoutItem[]} row
 * @param {IpGroupRole[]} roles
 * @returns {IpGroupLayoutItem[]}
 */
function sortIpGroupRowByRole(row: IpGroupLayoutItem[], roles: IpGroupRole[]) {
  return [...row].sort((left, right) => {
    const leftIndex = left.role ? roles.indexOf(left.role) : -1;
    const rightIndex = right.role ? roles.indexOf(right.role) : -1;
    return leftIndex - rightIndex;
  });
}

/** transform 测量完整节点（含标题），行间只加 IP_GROUP_GAP，避免重复计算反缩放标题。 */
export function layoutIpGroupChildren(items: IpGroupLayoutItem[]) {
  const rows: IpGroupLayoutItem[][] = [];
  const basicRoles: IpGroupRole[][] = [['main', 'turnaround'], ['voice', 'settings']];
  basicRoles.forEach((roles) => {
    const row = items.filter((item) => item.role && roles.includes(item.role));
    if (row.length) rows.push(sortIpGroupRowByRole(row, roles));
  });
  // 素材行保留 blocks 相对顺序，仅按每行 4 个切分。
  const materials = items.filter((item) => !item.role || item.role === 'material');
  for (let index = 0; index < materials.length; index += 4) rows.push(materials.slice(index, index + 4));
  let y = IP_GROUP_PADDING;
  return rows.flatMap((row) => {
    let x = IP_GROUP_PADDING;
    const positions = row.map((item) => {
      const position = { id: item.id, x: x + item.width / 2, y };
      x += item.width + IP_GROUP_GAP;
      return position;
    });
    y += Math.max(...row.map((item) => item.height)) + IP_GROUP_GAP;
    return positions;
  });
}

/** 首次加载使用原节点默认尺寸占位，挂载后由群组按各节点实测尺寸重排。 */
export function getIpGroupChildLayout(role: IpGroupRole, materialIndex = 0) {
  let column = role === 'turnaround' || role === 'settings' ? 1 : 0;
  let row = role === 'voice' || role === 'settings' ? 1 : 0;
  if (role === 'material') {
    column = materialIndex % 4;
    row = 2 + Math.floor(materialIndex / 4);
  }
  return {
    x: IP_GROUP_PADDING + column * (240 + IP_GROUP_GAP),
    y: IP_GROUP_PADDING + row * (240 + IP_GROUP_GAP + IP_GROUP_HEADER_HEIGHT),
    width: 240,
    height: 240 + IP_GROUP_HEADER_HEIGHT,
  };
}

/** 创建时按原节点默认尺寸估算群组占位，后续以实际测量为准；包含左右端口外溢和群组内边距。 */
export function getIpGroupInitialSize(materialCount: number) {
  const columns = Math.max(2, Math.min(4, materialCount));
  // 基础设定文本始终占第二行，材料从第三行起按每行 4 个估算。
  const rows = 2 + Math.ceil(materialCount / 4);
  return {
    width: columns * 240 + (columns - 1) * IP_GROUP_GAP + IP_GROUP_PADDING * 2,
    height: rows * (240 + IP_GROUP_HEADER_HEIGHT) + (rows - 1) * IP_GROUP_GAP + IP_GROUP_PADDING * 2,
  };
}
